import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@workspace/amarmeds-ds/components/ui/badge";
import { Button } from "@workspace/amarmeds-ds/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@workspace/amarmeds-ds/components/ui/card";
import { Input } from "@workspace/amarmeds-ds/components/ui/input";

import { api, ApiError, type OpsOrder, type Rider } from "../api";
import { parseTaka, taka } from "../format";

// Build a run from ready orders, and reconcile riders' cash.
export default function OpsRiders() {
  const qc = useQueryClient();
  const riders = useQuery({ queryKey: ["riders"], queryFn: () => api<{ riders: Rider[] }>("/ops/riders"), refetchInterval: 30_000 });
  const ready = useQuery({ queryKey: ["ops-orders", "ready"], queryFn: () => api<{ orders: OpsOrder[] }>("/ops/orders?status=ready"), refetchInterval: 15_000 });
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [riderId, setRiderId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const build = useMutation({
    mutationFn: () => api<{ runId: number }>("/ops/runs", { body: { riderId, orderIds: [...picked] } }),
    onSuccess: () => {
      setPicked(new Set());
      setError(null);
      void qc.invalidateQueries();
    },
    onError: (err) =>
      setError(
        err instanceof ApiError
          ? err.code === "cash_limit"
            ? `This rider holds ${taka(Number(err.body.cashHeldPaisa))}, over the ${taka(Number(err.body.cashLimitPaisa))} limit. Record a deposit first.`
            : err.code === "one_pharmacy_per_run"
              ? "A run takes orders from one pharmacy only."
              : err.code.replace(/_/g, " ")
          : "Could not build the run.",
      ),
  });

  const readyOrders = ready.data?.orders ?? [];
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Build a run</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {readyOrders.length === 0 && <p className="text-sm text-muted-foreground">No orders are ready for a rider.</p>}
          {readyOrders.map((o) => (
            <label key={o.orderId} className="flex items-center gap-3 text-sm">
              <input
                type="checkbox"
                checked={picked.has(o.orderId)}
                onChange={(e) =>
                  setPicked((s) => {
                    const n = new Set(s);
                    if (e.target.checked) n.add(o.orderId);
                    else n.delete(o.orderId);
                    return n;
                  })
                }
              />
              <span className="font-medium">{o.orderId}</span>
              <span className="text-muted-foreground">
                {o.zone} · pharmacy {o.pharmacyId} · {o.paymentMethod === "cod" ? `collect ${taka(o.totalPaisa)}` : "paid online"}
              </span>
            </label>
          ))}
          <div className="flex flex-wrap items-center gap-2">
            <select className="min-h-9 rounded-md border bg-background px-2" value={riderId ?? ""} onChange={(e) => setRiderId(e.target.value ? Number(e.target.value) : null)}>
              <option value="">Choose a rider</option>
              {riders.data?.riders
                .filter((r) => r.active)
                .map((r) => (
                  <option key={r.riderId} value={r.riderId} disabled={!!r.activeRun}>
                    {r.name}
                    {r.activeRun ? " (on a run)" : ""}
                  </option>
                ))}
            </select>
            <Button disabled={!riderId || picked.size === 0 || build.isPending} onClick={() => build.mutate()}>
              Assign {picked.size || ""} order{picked.size === 1 ? "" : "s"}
            </Button>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Riders and cash</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {riders.data?.riders.map((r) => (
            <RiderRow key={r.riderId} rider={r} />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function RiderRow({ rider }: { rider: Rider }) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("bkash");
  const [reference, setReference] = useState("");
  const deposit = useMutation({
    mutationFn: () => api(`/ops/riders/${rider.riderId}/deposits`, { body: { amountPaisa: parseTaka(amount), method, reference } }),
    onSuccess: () => {
      setAmount("");
      setReference("");
      void qc.invalidateQueries({ queryKey: ["riders"] });
    },
  });
  const over = rider.cashHeldPaisa > rider.cashLimitPaisa;
  return (
    <div className="flex flex-wrap items-center gap-3 border-b pb-3 text-sm last:border-0">
      <div className="min-w-40 flex-1">
        <div className="font-medium">{rider.name}</div>
        <div className="text-muted-foreground">{rider.phone || "no phone"}</div>
      </div>
      <Badge variant={over ? "destructive" : "secondary"}>
        Holds {taka(rider.cashHeldPaisa)} / {taka(rider.cashLimitPaisa)}
      </Badge>
      {rider.activeRun && <Badge variant="outline">Run {rider.activeRun.runId}: {rider.activeRun.status}</Badge>}
      <div className="flex items-center gap-1">
        <Input className="w-24" placeholder="৳" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        <select className="min-h-9 rounded-md border bg-background px-2" value={method} onChange={(e) => setMethod(e.target.value)}>
          <option value="bkash">bKash</option>
          <option value="bank">Bank</option>
          <option value="cash">Cash</option>
        </select>
        <Input className="w-28" placeholder="Ref" value={reference} onChange={(e) => setReference(e.target.value)} />
        <Button size="sm" disabled={parseTaka(amount) === null || deposit.isPending} onClick={() => deposit.mutate()}>
          Record deposit
        </Button>
      </div>
    </div>
  );
}
